import { Badge, ManageableItemsList, RichTextarea } from '@payaca/ambrosia';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import {
  EffectivePaymentScheduleStage,
  PaymentScheduleExplainer,
} from '@payaca/types/payment-schedule';
import { useMemo } from 'react';

/**
 * A stage whose amount is already resolved, in minor units. A grant stage
 * deducts a fixed amount rather than a share of the total, so it carries no
 * percentage.
 */
export type PaymentScheduleSummaryStage = Pick<
  EffectivePaymentScheduleStage,
  'description' | 'percentageDue' | 'value'
> &
  Partial<Pick<EffectivePaymentScheduleStage, 'isGrant' | 'isPaid'>>;

export type ValuedPaymentSchedule = {
  stages: PaymentScheduleSummaryStage[];
  explainer?: PaymentScheduleExplainer;
};

type Row = PaymentScheduleSummaryStage & { id: number };

type Props = ValuedPaymentSchedule & {
  region: AccountRegions;
  /** Omits the amount column, for a schedule with no total to value against. */
  hideValues?: boolean;
};

/** A grant deducts a fixed amount rather than a share of the total. */
const percentageDueLabel = (stage: PaymentScheduleSummaryStage) => {
  if (stage.isGrant) return '—';
  if (!stage.percentageDue) return null;
  return `${stage.percentageDue.toFixed(1)}%`;
};

const ValuedPaymentScheduleSummary = (props: Props) => {
  const { stages, explainer, region, hideValues = false } = props;

  const rows = useMemo(
    () => stages.map((stage, index) => ({ ...stage, id: index })),
    [stages]
  );

  const currencySymbol = useMemo(
    () => getRegionalTextString(region, RegionalStrings.CURRENCY_SYMBOL),
    [region]
  );

  return (
    <ManageableItemsList>
      <ManageableItemsList.Table
        uniqueKey="id"
        items={rows}
        hideEmptyState
        doNotUseFooterRow={
          explainer ? (
            <div className="mx-6 my-2">
              <RichTextarea readOnly value={explainer} />
            </div>
          ) : undefined
        }
      >
        <ManageableItemsList.Table.Column<Row, 'description'>
          field="description"
          header="Stage"
          className="!whitespace-normal"
          render={(field, row) => (
            <span className="flex flex-wrap items-center gap-2">
              <span className={row.isPaid ? 'text-gray-400' : undefined}>
                {field}
              </span>
              {row.isGrant && (
                <Badge colour="green" variant="soft">
                  Grant
                </Badge>
              )}
            </span>
          )}
        />
        <ManageableItemsList.Table.Column<Row, 'percentageDue'>
          field="percentageDue"
          header="% Due"
          className="text-right"
          render={(_field, row) => (
            <span className={row.isPaid ? 'text-gray-400' : undefined}>
              {percentageDueLabel(row)}
            </span>
          )}
        />
        {!hideValues && (
          <ManageableItemsList.Table.Column<Row, 'value'>
            field="value"
            className="text-right"
            header={`${currencySymbol} Due`}
            render={(field, row) =>
              row.isPaid ? (
                <>
                  <Badge colour="green" variant="soft">
                    Paid
                  </Badge>
                  <span className="pl-3 text-gray-400">
                    {currencyPrice(field, region)}
                  </span>
                </>
              ) : (
                currencyPrice(field, region)
              )
            }
          />
        )}
      </ManageableItemsList.Table>
    </ManageableItemsList>
  );
};

export default ValuedPaymentScheduleSummary;
